import { Link } from '@tanstack/react-router';
import { useEffect } from 'react';

import { Checkbox } from '~/components/ui/checkbox';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { useConnectionDeliveryLocales } from '~/hooks/useConnectionDeliveryLocales';
import { useTranslationLocales } from '~/hooks/useTranslationLocales';

interface TargetLocalesSelectProps {
  /** Selected locale codes, e.g. `['en-GB', 'es-419']`. Empty = English (en-US) only. */
  value: string[];
  onChange: (locales: string[]) => void;
  disabled?: boolean;
  /** Prefix for the checkbox ids, so two pickers on one page don't collide. */
  idPrefix?: string;
  /** `data-testid` root (default `target-locales-select`); derives `-checkbox-${code}`, `-enable-languages-link`, `-hint`, `-loading`. */
  testId?: string;
}

/**
 * Picks the extra locales GMC content is delivered in. English is always enriched; each ticked
 * locale is translated from it (one fast model call per product per locale). Offers only the
 * locales the GMC connection can deliver (`config.locale_destinations`) — the connection is the
 * source of truth, so a run never translates a language delivery would skip. Renders nothing for
 * users who cannot translate.
 */
export function TargetLocalesSelect({
  value,
  onChange,
  disabled = false,
  idPrefix = 'target-locale',
  testId = 'target-locales-select',
}: TargetLocalesSelectProps) {
  const canTranslate = useCanTranslateContent();
  const locales = useTranslationLocales(canTranslate);
  const delivery = useConnectionDeliveryLocales();
  const deliverable = new Set(delivery.codes.map((code) => code.toLowerCase()));
  const deliverableKey = [...deliverable].sort().join(',');
  const ready = locales.isSuccess && delivery.connectionsLoaded;

  // Drop a selection the connection can no longer deliver (e.g. a store default or a language
  // removed from the connection), so the submitted run matches what delivery will ship.
  useEffect(() => {
    if (!ready) return;
    const kept = value.filter((code) => deliverable.has(code.toLowerCase()));
    if (kept.length !== value.length) onChange(kept);
    // `deliverableKey` stands in for the Set, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, value, deliverableKey, onChange]);

  if (!canTranslate) return null;

  const list = (locales.data ?? []).filter((l) => deliverable.has(l.code.toLowerCase()));
  const toggle = (code: string) => {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  };

  let hint = 'English (en-US) is always enriched. Each ticked locale is translated from it.';
  // Shown instead of checkboxes when the connection enables no language.
  let notEnabled = false;
  if (locales.isError || delivery.isError)
    hint = 'Could not load locales — the run is English only.';
  else if (locales.isSuccess && locales.data.length === 0)
    hint = 'No translation locales ship in this deployment.';
  else if (ready && list.length === 0) {
    hint =
      'English (en-US) only. No other language is set up for delivery on the Google Merchant Center connection.';
    notEnabled = true;
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      <div>
        <span className="text-foreground block text-sm font-semibold">Languages</span>
        <span className="text-muted-foreground block text-xs" data-testid={`${testId}-hint`}>
          {hint}
          {notEnabled && delivery.connectionId && (
            <>
              {' '}
              <Link
                to="/onboarding/integration/google/google-merchant-center"
                search={{ connectionId: delivery.connectionId }}
                data-testid={`${testId}-enable-languages-link`}
                className="text-primary cursor-pointer underline-offset-2 hover:underline"
              >
                Enable languages on the connection
              </Link>
            </>
          )}
        </span>
      </div>
      {(locales.isPending || delivery.isPending) && (
        <span className="text-muted-foreground text-xs" data-testid={`${testId}-loading`}>
          Loading…
        </span>
      )}
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {list.map((l) => {
          const id = `${idPrefix}-${l.code}`;
          return (
            <div key={l.code} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={value.includes(l.code)}
                data-testid={`${testId}-checkbox-${l.code}`}
                disabled={disabled}
                className="cursor-pointer disabled:cursor-not-allowed"
                onCheckedChange={() => {
                  toggle(l.code);
                }}
              />
              <label
                htmlFor={id}
                className={`text-sm select-none ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {l.displayName}
                <span className="text-muted-foreground ml-1 text-xs">{l.code}</span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
